import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Brain,
  Gavel,
  Zap,
  Heart,
  DollarSign,
  Scale,
  Search,
  Send,
  User,
  FileText,
  Download,
  CheckCircle,
  ChevronDown,
} from "lucide-react";

// --- CONFIGURATION ---
const AGENT_TEXT =
  "Based on my analysis, I believe the premise is fundamentally flawed. We must consider the long-term implications of such actions on the broader ecosystem. Furthermore, historical data suggests a high probability of unintended consequences that have not yet been factored into the current model. A deeper, multi-variable risk assessment is immediately required before proceeding further.";

// Math: (Characters * Speed) + 3 Seconds Reading Time
const TYPING_SPEED_MS = 20;
const READING_DELAY_MS = 3000;
const TOTAL_STEP_DURATION =
  AGENT_TEXT.length * TYPING_SPEED_MS + READING_DELAY_MS;

const SUBJECTS = [
  "SCIENCE",
  "LAWS",
  "HEALTH",
  "BUSINESS",
  "LIFE",
  "LOVE",
  "AITAH",
];

const AGENTS = [
  {
    id: "agent-1",
    name: "The Skeptic",
    role: "Critical Analysis",
    icon: Scale,
    color: "cyan",
    glow: "shadow-cyan-500/20",
  },
  {
    id: "agent-2",
    name: "The Visionary",
    role: "Future Tech",
    icon: Zap,
    color: "cyan",
    glow: "shadow-cyan-500/20",
  },
  {
    id: "agent-3",
    name: "The Realist",
    role: "Pragmatism",
    icon: Gavel,
    color: "cyan",
    glow: "shadow-cyan-500/20",
  },
  {
    id: "agent-4",
    name: "The Humanist",
    role: "Ethics & Society",
    icon: Heart,
    color: "cyan",
    glow: "shadow-cyan-500/20",
  },
  {
    id: "agent-5",
    name: "The Capitalist",
    role: "Economics",
    icon: DollarSign,
    color: "cyan",
    glow: "shadow-cyan-500/20",
  },
  {
    id: "agent-6",
    name: "The Strategist",
    role: "Game Theory",
    icon: Brain,
    color: "cyan",
    glow: "shadow-cyan-500/20",
  },
  {
    id: "researcher",
    name: "The Researcher",
    role: "Fact Verification",
    icon: Search,
    color: "yellow",
    glow: "shadow-yellow-500/40",
    special: true,
  },
];

export default function DebateArena() {
  const [activeSpeakerId, setActiveSpeakerId] = useState(null);
  const [simulationStep, setSimulationStep] = useState(0);
  const [statusMode, setStatusMode] = useState("IDLE");
  const [inputText, setInputText] = useState("");
  const [lastQuestion, setLastQuestion] = useState(
    "Why did the chicken cross the road if AI allows it to teleport?"
  );
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // SUBJECT STATE
  const [currentSubject, setCurrentSubject] = useState("SCIENCE");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Mouse Tracking
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // --- SEQUENCER ---
  useEffect(() => {
    let timeoutId;
    const runStep = () => {
      if (simulationStep >= AGENTS.length) {
        setStatusMode("VERDICT");
        setActiveSpeakerId(null);
        return;
      }
      const currentAgent = AGENTS[simulationStep];
      setActiveSpeakerId(currentAgent.id);
      setStatusMode("SPEAKING");

      timeoutId = setTimeout(() => {
        setActiveSpeakerId(null);
        setStatusMode("IDLE");

        timeoutId = setTimeout(() => {
          setSimulationStep((prev) => prev + 1);
        }, 1200);
      }, TOTAL_STEP_DURATION);
    };
    runStep();
    return () => clearTimeout(timeoutId);
  }, [simulationStep]);

  const handleSubmit = () => {
    if (!inputText) return;
    setLastQuestion(inputText);
    setInputText("");
    setStatusMode("IDLE");
    setSimulationStep(0);
    setActiveSpeakerId(null);
  };

  const humanAgent = {
    id: "human",
    name: "The Human",
    role: lastQuestion,
    icon: User,
    color: "cyan",
    special: false,
    isHuman: true,
  };

  return (
    <div className="min-h-screen bg-black text-white font-sans overflow-hidden relative selection:bg-cyan-500/30">
      {/* --- BACKGROUND LAYERS --- */}
      <div className="absolute inset-0 z-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] opacity-20"></div>
      <div
        className="absolute inset-0 z-0 pointer-events-none transition-opacity duration-500"
        style={{
          background: `radial-gradient(600px circle at ${mousePos.x}px ${mousePos.y}px, rgba(6, 182, 212, 0.25), transparent 40%)`,
        }}
      ></div>
      <div
        className="absolute inset-0 z-0 pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(to right, #ffffff15 1px, transparent 1px), linear-gradient(to bottom, #ffffff15 1px, transparent 1px)`,
          backgroundSize: "24px 24px",
          maskImage: `radial-gradient(300px circle at ${mousePos.x}px ${mousePos.y}px, black, transparent)`,
          WebkitMaskImage: `radial-gradient(300px circle at ${mousePos.x}px ${mousePos.y}px, black, transparent)`,
        }}
      ></div>

      {/* --- MAIN UI --- */}
      <div className="relative z-10 h-screen flex flex-col p-6">
        {/* --- HEADER --- */}
        <div className="w-full flex flex-col items-center justify-center mb-4 z-[60] relative">
          <h1
            className="text-7xl md:text-8xl font-black tracking-wide text-transparent bg-clip-text bg-gradient-to-b from-white to-zinc-500 uppercase"
            style={{
              fontFamily: "Impact, sans-serif",
              textShadow:
                "0 0 3px rgba(255,255,255,0.9), 0 0 15px rgba(6,182,212,0.7), 0 0 40px rgba(6,182,212,0.3)",
            }}
          >
            CONCURRENCE
          </h1>

          {/* SLOGAN + DROPDOWN SUBJECT */}
          {/* FIX: Added 'ml-8' to shift the entire block right for visual centering */}
          <div className="relative mt-2 ml-8 flex items-center justify-center gap-4">
            <div className="absolute -inset-1 bg-cyan-500 blur-lg opacity-20"></div>

            {/* 1. Slogan Text */}
            <p
              className="relative text-xl md:text-2xl font-bold text-cyan-400 tracking-[0.3em] uppercase"
              style={{ textShadow: "0 0 10px rgba(6,182,212,0.8)" }}
            >
              GREAT AI MINDS THINK...
            </p>

            {/* 2. Interactive Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setIsDropdownOpen(true)}
              onMouseLeave={() => setIsDropdownOpen(false)}
            >
              {/* The Active Subject */}
              <div className="cursor-pointer group flex items-center gap-2">
                <p
                  className="relative text-xl md:text-2xl font-bold text-cyan-400 tracking-[0.3em] uppercase decoration-cyan-500/50 underline-offset-8 group-hover:underline"
                  style={{ textShadow: "0 0 10px rgba(6,182,212,0.8)" }}
                >
                  {currentSubject}
                </p>
                <ChevronDown
                  className={`w-5 h-5 text-cyan-500 transition-transform duration-300 ${
                    isDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </div>

              {/* The Dropdown Menu */}
              <AnimatePresence>
                {isDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 pt-4 z-[100]"
                  >
                    <div className="bg-black/90 border border-cyan-900/50 backdrop-blur-md rounded-lg p-2 min-w-[200px] shadow-[0_0_30px_rgba(6,182,212,0.2)] flex flex-col gap-1">
                      {SUBJECTS.filter((s) => s !== currentSubject).map(
                        (subject) => (
                          <button
                            key={subject}
                            onClick={() => {
                              setCurrentSubject(subject);
                              setIsDropdownOpen(false);
                            }}
                            className="text-left px-4 py-3 hover:bg-cyan-900/30 rounded text-xl font-bold text-cyan-400 tracking-[0.3em] uppercase transition-colors"
                            style={{
                              textShadow: "0 0 5px rgba(6,182,212,0.5)",
                            }}
                          >
                            {subject}
                          </button>
                        )
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* ROUND INDICATOR */}
          <div className="mt-4 flex items-center gap-4">
            <div className="h-[1px] w-12 bg-zinc-800"></div>
            <div className="px-4 py-1 border border-zinc-800 bg-zinc-900/50 rounded text-zinc-400 font-mono text-xs tracking-widest uppercase">
              ROUND 1
            </div>
            <div className="h-[1px] w-12 bg-zinc-800"></div>
          </div>
        </div>

        {/* --- ARENA --- */}
        <div className="flex-1 w-full max-w-7xl mx-auto flex flex-col justify-center">
          <div className="grid grid-cols-4 gap-6 h-[60vh] items-center">
            {/* LEFT BANK */}
            <div className="col-span-1 flex flex-col gap-4">
              <NeonCornerCard agent={humanAgent} isActive={false} />
              {AGENTS.slice(0, 3).map((agent) => (
                <NeonCornerCard
                  key={agent.id}
                  agent={agent}
                  isActive={activeSpeakerId === agent.id}
                />
              ))}
            </div>

            {/* CENTER STAGE */}
            <div className="col-span-2 h-full flex items-center justify-center relative z-40">
              <AnimatePresence mode="popLayout">
                {statusMode === "SPEAKING" && activeSpeakerId && (
                  <ActiveSpeakerCard
                    key={activeSpeakerId}
                    agent={AGENTS.find((a) => a.id === activeSpeakerId)}
                  />
                )}
                {statusMode === "VERDICT" && <FinalVerdictCard key="verdict" />}
                {statusMode === "IDLE" && (
                  <motion.div
                    key="idle"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute text-zinc-600 text-sm font-mono animate-pulse"
                  >
                    PROCESSING ARGUMENTS...
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* RIGHT BANK */}
            <div className="col-span-1 flex flex-col gap-4">
              <NeonCornerCard
                key={AGENTS[6].id}
                agent={AGENTS[6]}
                isActive={activeSpeakerId === AGENTS[6].id}
              />
              {AGENTS.slice(3, 6).map((agent) => (
                <NeonCornerCard
                  key={agent.id}
                  agent={agent}
                  isActive={activeSpeakerId === agent.id}
                />
              ))}
            </div>
          </div>
        </div>

        {/* --- INPUT --- */}
        <div className="w-full max-w-3xl mx-auto mt-4">
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg blur opacity-30 group-hover:opacity-75 transition duration-1000 group-hover:duration-200"></div>
            <div className="relative flex items-center bg-zinc-900 rounded-lg border border-zinc-800 p-2">
              <input
                type="text"
                className="w-full bg-transparent text-lg px-4 py-3 outline-none text-white placeholder-zinc-500 font-bold tracking-wide"
                placeholder="ENTER PROMPT..."
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              />
              <button
                onClick={handleSubmit}
                className="p-3 bg-cyan-600 hover:bg-cyan-500 text-white rounded-md transition-colors"
              >
                <Send size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- NEON CORNER CARD ---
function NeonCornerCard({ agent, isActive }) {
  const cardRef = useRef(null);
  const [closestCorner, setClosestCorner] = useState(null);
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    setIsHovered(true);
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const distTL = Math.hypot(x, y);
    const distTR = Math.hypot(rect.width - x, y);
    const distBL = Math.hypot(x, rect.height - y);
    const distBR = Math.hypot(rect.width - x, rect.height - y);
    const min = Math.min(distTL, distTR, distBL, distBR);
    if (min === distTL) setClosestCorner("TL");
    else if (min === distTR) setClosestCorner("TR");
    else if (min === distBL) setClosestCorner("BL");
    else if (min === distBR) setClosestCorner("BR");
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setClosestCorner(null);
  };

  const neonColor = agent.special ? "text-yellow-400" : "text-cyan-400";
  const neonBg = agent.special ? "bg-yellow-400" : "bg-cyan-400";

  return (
    <div className="h-28 w-full relative">
      {isActive ? (
        <div className="w-full h-full opacity-0" />
      ) : (
        <motion.div
          layoutId={`card-${agent.id}`}
          ref={cardRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          className={`relative w-full h-full bg-zinc-900 border border-zinc-800 p-4 rounded-xl flex flex-col items-center justify-center gap-2 cursor-pointer overflow-hidden transition-all duration-300 ${
            isHovered ? "bg-zinc-800" : ""
          }`}
        >
          {/* Neon Borders */}
          <div
            className={`absolute top-0 left-0 w-1/2 h-[2px] ${neonBg} transition-all duration-300 ${
              isHovered && closestCorner === "TL"
                ? "opacity-100 shadow-[0_0_10px_currentColor]"
                : "opacity-0"
            }`}
          />
          <div
            className={`absolute top-0 left-0 h-1/2 w-[2px] ${neonBg} transition-all duration-300 ${
              isHovered && closestCorner === "TL"
                ? "opacity-100 shadow-[0_0_10px_currentColor]"
                : "opacity-0"
            }`}
          />
          <div
            className={`absolute top-0 right-0 w-1/2 h-[2px] ${neonBg} transition-all duration-300 ${
              isHovered && closestCorner === "TR"
                ? "opacity-100 shadow-[0_0_10px_currentColor]"
                : "opacity-0"
            }`}
          />
          <div
            className={`absolute top-0 right-0 h-1/2 w-[2px] ${neonBg} transition-all duration-300 ${
              isHovered && closestCorner === "TR"
                ? "opacity-100 shadow-[0_0_10px_currentColor]"
                : "opacity-0"
            }`}
          />
          <div
            className={`absolute bottom-0 left-0 w-1/2 h-[2px] ${neonBg} transition-all duration-300 ${
              isHovered && closestCorner === "BL"
                ? "opacity-100 shadow-[0_0_10px_currentColor]"
                : "opacity-0"
            }`}
          />
          <div
            className={`absolute bottom-0 left-0 h-1/2 w-[2px] ${neonBg} transition-all duration-300 ${
              isHovered && closestCorner === "BL"
                ? "opacity-100 shadow-[0_0_10px_currentColor]"
                : "opacity-0"
            }`}
          />
          <div
            className={`absolute bottom-0 right-0 w-1/2 h-[2px] ${neonBg} transition-all duration-300 ${
              isHovered && closestCorner === "BR"
                ? "opacity-100 shadow-[0_0_10px_currentColor]"
                : "opacity-0"
            }`}
          />
          <div
            className={`absolute bottom-0 right-0 h-1/2 w-[2px] ${neonBg} transition-all duration-300 ${
              isHovered && closestCorner === "BR"
                ? "opacity-100 shadow-[0_0_10px_currentColor]"
                : "opacity-0"
            }`}
          />

          {/* Content */}
          <agent.icon
            className={`w-6 h-6 text-zinc-400 transition-colors ${
              isHovered ? neonColor : ""
            }`}
          />
          <div className="text-center w-full px-2">
            <div
              className={`text-zinc-200 font-bold text-sm transition-colors ${
                isHovered ? "text-white" : ""
              }`}
            >
              {agent.name}
            </div>
            {agent.isHuman ? (
              <div className="text-zinc-500 text-[10px] uppercase tracking-wider w-full truncate">
                {agent.role}
              </div>
            ) : (
              <div className="text-zinc-500 text-[10px] uppercase tracking-wider">
                {agent.role}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
}

// --- ACTIVE SPEAKER CARD ---
function ActiveSpeakerCard({ agent }) {
  const borderColor = agent.special ? "border-yellow-400" : "border-cyan-500";
  const shadowColor = agent.special
    ? "shadow-yellow-500/20"
    : "shadow-cyan-500/20";
  const textColor = agent.special ? "text-yellow-400" : "text-cyan-400";
  const pulseColor = agent.special ? "#EAB308" : "#06B6D4";

  return (
    <motion.div
      layoutId={`card-${agent.id}`}
      className={`absolute w-full max-w-2xl bg-black border-2 ${borderColor} shadow-2xl rounded-2xl p-8 z-50 flex flex-col gap-4 overflow-hidden`}
      transition={{
        type: "spring",
        stiffness: 180,
        damping: 25,
      }}
    >
      {/* PULSE LAYER */}
      <motion.div
        className={`absolute inset-0 z-0 opacity-20 pointer-events-none`}
        animate={{
          boxShadow: [
            `inset 0 0 0px ${pulseColor}00`,
            `inset 0 0 50px ${pulseColor}`,
            `inset 0 0 0px ${pulseColor}00`,
          ],
        }}
        exit={{ opacity: 0, transition: { duration: 0.2 } }}
        transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
      />

      {/* CONTENT LAYER */}
      <motion.div
        className="relative z-10 flex flex-col gap-4 h-full"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="flex items-center gap-4 border-b border-zinc-800 pb-4">
          <div className={`p-3 rounded-full bg-zinc-900 border ${borderColor}`}>
            <agent.icon className="w-8 h-8 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">{agent.name}</h2>
            <span className={`${textColor} text-xs font-mono animate-pulse`}>
              ● SPEAKING NOW
            </span>
          </div>
        </div>

        <div className="text-zinc-300 text-lg leading-relaxed font-light min-h-[100px]">
          <TypewriterEffect text={AGENT_TEXT} />
        </div>

        <div className="flex justify-between items-center text-xs text-zinc-500 mt-4 font-mono">
          <span>Thinking Time: 0.4s</span>
          <span>Confidence: 94%</span>
        </div>
      </motion.div>
    </motion.div>
  );
}

function FinalVerdictCard() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="w-full max-w-2xl bg-zinc-950 border border-yellow-500/50 shadow-[0_0_50px_-12px_rgba(234,179,8,0.3)] rounded-2xl p-8 z-50 flex flex-col gap-6 relative overflow-hidden"
    >
      <div className="absolute top-0 right-0 p-32 bg-yellow-500/5 blur-3xl rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
      <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
            <FileText className="w-6 h-6 text-yellow-500" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">
              Final Expert Verdict
            </h2>
            <p className="text-yellow-500/80 text-xs font-mono">
              CONFIDENCE: 99.8% • VERIFIED BY RESEARCHER
            </p>
          </div>
        </div>
        <div className="bg-zinc-900 px-3 py-1 rounded-full border border-zinc-800 text-xs text-zinc-400">
          CASE #8291
        </div>
      </div>
      <div className="space-y-4 text-zinc-300 text-sm leading-relaxed max-h-[40vh] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-zinc-700">
        <p>
          <strong className="text-white">Executive Summary:</strong> The council
          has concluded that the chicken's motivation was primarily driven by
          biological imperatives, specifically foraging opportunities detected
          on the opposing vector.
        </p>
        <div className="bg-zinc-900/50 p-4 rounded-lg border border-zinc-800/50">
          <h3 className="text-cyan-400 font-bold mb-2 flex items-center gap-2">
            <CheckCircle size={14} /> Key Recommendations
          </h3>
          <ul className="list-disc pl-4 space-y-1 text-zinc-400">
            <li>Do not anthropomorphize poultry decision-making matrices.</li>
            <li>
              Consider the "Teleportation" variable as a theoretical outlier
              (See: Agent 2).
            </li>
            <li>
              Secure the perimeter to prevent further unauthorized crossings.
            </li>
          </ul>
        </div>
        <p>
          <strong className="text-white">Expert Tip:</strong> For future
          logistics, consider the road density coefficient before deploying
          assets.
        </p>
      </div>
      <div className="pt-4 border-t border-zinc-800 flex gap-3">
        <button className="flex-1 bg-yellow-600 hover:bg-yellow-500 text-black font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition-colors">
          <Download size={18} />
          Download PDF Report
        </button>
        <button className="px-6 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg border border-zinc-700 transition-colors">
          Share
        </button>
      </div>
    </motion.div>
  );
}

function TypewriterEffect({ text }) {
  const [displayed, setDisplayed] = useState("");
  useEffect(() => {
    setDisplayed("");
    let i = 0;
    const timer = setInterval(() => {
      if (i < text.length) {
        setDisplayed((prev) => prev + text.charAt(i));
        i++;
      } else {
        clearInterval(timer);
      }
    }, 20);
    return () => clearInterval(timer);
  }, [text]);
  return (
    <span>
      {displayed}
      <span className="inline-block w-2 h-5 bg-cyan-500 ml-1 animate-pulse align-middle" />
    </span>
  );
}
